import { i18n, LOCALE_PAR_DEFAUT, type LocaleDisponible } from '@/plugins/i18n'

/**
 * Traduction des libellés qui viennent de la base, et non des fichiers de traduction.
 *
 * Les libellés de l'interface sont figés dans `src/assets/translations` et servis par vue-i18n.
 * Ceux du référentiel — domaines, sous-domaines, compétences, types d'ouvrage — sont chargés depuis
 * l'API, et les tables qui le permettent portent une colonne anglaise à côté de la française
 * (`domaine_lib` / `domaine_lib_en`, `nom` / `nom_en`, `cpt_lib` / `cpt_lib_en`,
 * `type_ouvrage_lib` / `type_ouvrage_lib_en`). Ces colonnes sont facultatives : une entrée créée
 * depuis l'administration peut n'exister qu'en français.
 *
 * D'où la règle appliquée ici : on affiche la langue courante quand elle est renseignée, l'autre
 * sinon. Mieux vaut un libellé dans la mauvaise langue qu'une ligne vide dans une arborescence.
 */

/** Suffixe des champs anglais tel que l'API les nomme (`typeOuvrageLib` -> `typeOuvrageLibEn`). */
const SUFFIXE_EN = 'En'

function texteUtile(valeur: unknown): string | null {
  if (typeof valeur !== 'string') return null
  const texte = valeur.trim()
  return texte.length > 0 ? texte : null
}

/** Langue de l'interface, ramenée aux langues que l'API sait servir. */
export function localeCourante(): LocaleDisponible {
  const locale = i18n.locale.value
  return locale === 'en' || locale === 'fr' ? locale : LOCALE_PAR_DEFAUT
}

/**
 * Libellé à afficher entre une version française et une version anglaise.
 *
 * Appelé dans un `computed` ou un template, il suit les changements de langue : la locale est lue
 * sur l'instance vue-i18n, qui est réactive.
 *
 * @param fr Libellé français (colonne de référence).
 * @param en Libellé anglais, souvent absent.
 * @param locale Langue forcée — réservée aux tests et aux exports, où la langue n'est pas celle de
 *   l'interface.
 */
export function libelleDynamique(fr: unknown, en: unknown, locale?: LocaleDisponible): string {
  const langue = locale ?? localeCourante()
  const francais = texteUtile(fr)
  const anglais = texteUtile(en)

  if (langue === 'en') return anglais ?? francais ?? ''
  return francais ?? anglais ?? ''
}

/**
 * Même chose, en déduisant les deux champs d'un enregistrement d'API à partir du nom du champ
 * français : `libelleDynamiqueDe(row, 'cptLib')` lit `cptLib` et `cptLibEn`.
 *
 * Les deux casses sont acceptées, l'API servant tantôt `typeOuvrageLib`, tantôt `type_ouvrage_lib`
 * selon les endpoints.
 */
export function libelleDynamiqueDe(
  enregistrement: Record<string, unknown> | null | undefined,
  champFr: string,
  locale?: LocaleDisponible,
): string {
  if (!enregistrement || typeof enregistrement !== 'object') return ''

  const champEn = `${champFr}${SUFFIXE_EN}`
  const snake = (champ: string): string =>
    champ.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase()

  const fr = enregistrement[champFr] ?? enregistrement[snake(champFr)]
  const en = enregistrement[champEn] ?? enregistrement[snake(champEn)]

  return libelleDynamique(fr, en, locale)
}
